import { useMemo, useState } from "react";
import styles from "../styles/ContactForm.module.css";
import Button from "./Button";

const SUBJECT_LABELS = {
  "note-ninja": "Note Ninja support",
  "record-health": "Record Health support",
  collaboration: "Collaboration",
  other: "Other",
};

function isValidEmail(email) {
  // Not RFC-perfect (doesn't need to be), but catches common mistakes.
  // Also works well with type="email" + browser validation.
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export default function ContactForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "note-ninja",
    message: "",
    company: "", // honeypot (keep it in state so it gets posted)
  });

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [touched, setTouched] = useState({
    name: false,
    email: false,
    message: false,
  });

  const [submitError, setSubmitError] = useState("");

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function onBlur(e) {
    const { name } = e.target;
    if (name in touched) {
      setTouched((prev) => ({ ...prev, [name]: true }));
    }
  }

  const errors = useMemo(() => {
    const next = {};

    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!isValidEmail(form.email))
      next.email = "Please enter a valid email.";

    if (!form.message.trim()) next.message = "Please enter a message.";
    else if (form.message.trim().length > 4000)
      next.message = "Message is too long (max 4000 characters).";

    return next;
  }, [form.name, form.email, form.message]);

  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    // mark all required fields as touched so errors show immediately
    setTouched({ name: true, email: true, message: true });

    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);

    try {
      const res = await fetch("/api/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const contentType = res.headers.get("content-type") || "";
      const payload = contentType.includes("application/json")
        ? await res.json().catch(() => null)
        : await res.text().catch(() => "");

      if (!res.ok) {
        const msg =
          (payload && payload.error) ||
          (typeof payload === "string" && payload) ||
          `Failed to send (HTTP ${res.status}).`;
        throw new Error(msg);
      }

      setSubmitted(true);
    } catch (err) {
      console.error("submit failed", err);
      setSubmitError(err instanceof Error ? err.message : "Failed to send.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSubmitted(false);
    setSubmitError("");
    setTouched({ name: false, email: false, message: false });
    setForm({
      name: "",
      email: "",
      subject: "note-ninja",
      message: "",
      company: "",
    });
  };

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <article className="card">
          {submitted ? (
            <div className={styles.success}>
              <h2 className={styles.title}>Message sent</h2>

              <p className={styles.successText}>
                Thanks — I got it. I'll reply as soon as I can.
              </p>

              <div className={styles.successMeta}>
                <div>
                  <span className={styles.metaLabel}>Subject</span>
                  <span className={styles.metaValue}>
                    {SUBJECT_LABELS[form.subject] || "Other"}
                  </span>
                </div>

                {form.email ? (
                  <div>
                    <span className={styles.metaLabel}>Reply to</span>
                    <span className={styles.metaValue}>{form.email}</span>
                  </div>
                ) : null}
              </div>

              <div className={styles.successActions}>
                <Button variant="btnBase" size="btnMedium" onClick={reset}>
                  Send another
                </Button>

                <Button href="/" variant="btnBase" size="btnMedium">
                  Back home
                </Button>
              </div>

              <p className={styles.note}>
                No spam — I only use your info to reply.
              </p>
            </div>
          ) : (
            <form className={styles.form} onSubmit={onSubmit} noValidate>
              <h2 className={styles.title}>Contact Form</h2>

              {submitError ? (
                <p className={styles.formError} role="alert">
                  {submitError}
                </p>
              ) : null}

              <div className={styles.grid2}>
                <div className={styles.field}>
                  <label className={styles.label} htmlFor="name">
                    Name <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    className={styles.input}
                    value={form.name}
                    onChange={onChange}
                    onBlur={onBlur}
                    autoComplete="name"
                    required
                    aria-invalid={touched.name && !!errors.name}
                    aria-describedby={errors.name ? "name-error" : undefined}
                  />
                  {touched.name && errors.name ? (
                    <p
                      id="name-error"
                      className={styles.fieldError}
                      role="alert"
                    >
                      {errors.name}
                    </p>
                  ) : null}
                </div>

                <div className={styles.field}>
                  <label className={styles.label} htmlFor="email">
                    Email <span aria-hidden="true">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    className={styles.input}
                    value={form.email}
                    onChange={onChange}
                    onBlur={onBlur}
                    autoComplete="email"
                    required
                    aria-invalid={touched.email && !!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {touched.email && errors.email ? (
                    <p
                      id="email-error"
                      className={styles.fieldError}
                      role="alert"
                    >
                      {errors.email}
                    </p>
                  ) : null}
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="subject">
                  Subject
                </label>
                <div className={styles.selectWrap}>
                  <select
                    id="subject"
                    name="subject"
                    className={styles.select}
                    value={form.subject}
                    onChange={onChange}
                  >
                    <option value="note-ninja">Note Ninja support</option>
                    <option value="record-health">Record Health support</option>
                    <option value="collaboration">Collaboration</option>
                    <option value="other">Other</option>
                  </select>

                  <span className={styles.chevron} aria-hidden="true">
                    ▾
                  </span>
                </div>
              </div>

              <div className={styles.field}>
                <label className={styles.label} htmlFor="message">
                  Message <span aria-hidden="true">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  className={styles.textarea}
                  value={form.message}
                  onChange={onChange}
                  onBlur={onBlur}
                  rows={6}
                  required
                  aria-invalid={touched.message && !!errors.message}
                  aria-describedby={
                    errors.message ? "message-error" : undefined
                  }
                />
                {touched.message && errors.message ? (
                  <p
                    id="message-error"
                    className={styles.fieldError}
                    role="alert"
                  >
                    {errors.message}
                  </p>
                ) : (
                  <p className={styles.helper}>
                    I'll only use your message details to respond.
                  </p>
                )}
              </div>

              {/* honeypot */}
              <input
                type="text"
                name="company"
                tabIndex="-1"
                autoComplete="off"
                style={{ display: "none" }}
                value={form.company}
                onChange={onChange}
              />

              <Button size="btnFull" type="submit" disabled={!canSubmit}>
                {submitting ? "Sending..." : "Send message"}
              </Button>

              <p className={styles.note}>
                No spam — I only use your info to reply.
              </p>
            </form>
          )}
        </article>
      </div>
    </section>
  );
}
